import {
  GENERIC_ERROR_MESSAGE,
  INCORRECT_EMAIL_OR_PASSWORD,
  TOO_MANY_TRIES,
} from 'common/constants/loginErrorMessages';
import {
  confirmPasswordReset,
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { hideLoading, showLoading } from 'v3/data/store/loading/actions';
import { RoutesEnum } from 'v3/routes/enum';

const BASE_URL = window.location.origin;

const errosTypes = {
  user: 'auth/user-not-found',
  password: 'auth/wrong-password',
  overRequests: 'auth/too-many-requests',
};

export const useFirebase = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const auth = getAuth();

  const logout = async () => {
    showLoading();
    try {
      await signOut(auth);
      navigate(RoutesEnum.Home, { replace: true });
    } catch (error) {
      console.error(error);
    } finally {
      hideLoading();
    }
  };

  const signIn = async (data: { user: string; password: string }) => {
    try {
      showLoading();
      await signInWithEmailAndPassword(auth, data.user, data.password);
      navigate(RoutesEnum.Home);
    } catch (error: any) {
      if (error.code === errosTypes.user || error.code === errosTypes.password) {
        setError(INCORRECT_EMAIL_OR_PASSWORD);
        throw Error;
      }
      if (error.code === errosTypes.overRequests) {
        setError(TOO_MANY_TRIES);
        throw Error;
      } else {
        setError(GENERIC_ERROR_MESSAGE);
        throw Error;
      }
    } finally {
      hideLoading();
    }
  };

  const sendEmailResetPassword = async (email: string) => {
    showLoading();

    const actionCodeSettings = {
      url: `${BASE_URL}${RoutesEnum.AuthResetPassword}`,
      handleCodeInApp: true,
    };

    try {
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
    } catch (error) {
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      hideLoading();
    }
  };

  const updatePasswordCurrentUser = async (newPassword: string) => {
    showLoading();
    try {
      const oobCode = searchParams.get('oobCode') ?? '';
      await confirmPasswordReset(auth, oobCode, newPassword);
      navigate(RoutesEnum.Home);
    } catch (error) {
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      hideLoading();
    }
  };

  const getToken = async (): Promise<string | null> => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        return token;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Erro ao obter o token:', error);
      return null;
    }
  };

  return {
    auth,
    error,
    logout,
    signIn,
    sendEmailResetPassword,
    updatePasswordCurrentUser,
    getToken,
  };
};
